<script>
    import Button from './Button.svelte';
    import Card from './Card.svelte';
    import Prose from './Prose.svelte';
    import { get } from 'svelte/store';
    import { page } from '$app/stores';
    import { createEventDispatcher } from 'svelte';
    import { get_is_admin } from './utils/is_admin';
    import { capture_supabase_error } from '$lib/utils/capture_supabase_error';

    let className = '';
    export { className as class };
    export let review = {};
    export let show_for = true;
    export let invert = false;

    const review_text = review.review_text || '';

    let collapse = review.review_text.length > 480;

    let text = collapse
        ? review_text.slice(0, review_text.slice(0, 320).lastIndexOf(' ')) +
          '...'
        : review_text;
    let collapsed = true;

    const handle_expand = () => {
        collapsed = false;
        text = review_text;
    };

    const dispatch = createEventDispatcher();
    const is_admin = get_is_admin();
    const supabase = get(page)?.data?.supabase;

    let toggling = false;
    let updating_priority = false;
    let updating_placements = false;
    let priority_value = review.priority ?? 0;

    $: priority_value = review.priority ?? 0;
    $: placement_home =
        review.User_Review_Placements?.some(
            (p) => p.placement === 'homepage'
        ) || false;
    $: placement_list =
        review.User_Review_Placements?.some(
            (p) => p.placement === 'product_list'
        ) || false;

    const toggle_approved = async () => {
        if (!supabase || toggling || !review?.id) return;

        toggling = true;
        const next_approved = !review.approved;

        const { error } = await supabase
            .from('User_Reviews')
            .update({ approved: next_approved })
            .eq('id', review.id);

        toggling = false;

        if (error) {
            console.error('toggle_approved failed', error);
            capture_supabase_error(error, {
                message: 'Review approval toggle failed',
                operation: 'update',
                table: 'User_Reviews'
            });
            return;
        }

        review = { ...review, approved: next_approved };
    };

    const update_priority = async (next_priority) => {
        if (!supabase || updating_priority || !review?.id) return;
        if (Number.isNaN(next_priority)) return;

        updating_priority = true;

        const { error } = await supabase
            .from('User_Reviews')
            .update({ priority: next_priority })
            .eq('id', review.id);

        updating_priority = false;

        if (error) {
            console.error('update_priority failed', error);
            capture_supabase_error(error, {
                message: 'Review priority update failed',
                operation: 'update',
                table: 'User_Reviews'
            });
            return;
        }

        review = { ...review, priority: next_priority };
        dispatch('priorityChange', { id: review.id, priority: next_priority });
    };

    const handle_priority_change = (event) => {
        const next_priority = Number(event.target.value);
        if (next_priority === review.priority) return;
        update_priority(next_priority);
    };

    const update_placement = async (placement, enabled) => {
        if (!supabase || updating_placements || !review?.id) return;
        updating_placements = true;

        let error = null;

        if (enabled) {
            const res = await supabase
                .from('User_Review_Placements')
                .upsert({ review_id: review.id, placement });
            error = res.error;
        } else {
            const res = await supabase
                .from('User_Review_Placements')
                .delete()
                .eq('review_id', review.id)
                .eq('placement', placement);
            error = res.error;
        }

        updating_placements = false;

        if (error) {
            console.error('update_placement failed', error);
            capture_supabase_error(error, {
                message: 'Review placement update failed',
                operation: enabled ? 'upsert' : 'delete',
                table: 'User_Review_Placements'
            });
            return;
        }

        const filtered =
            review.User_Review_Placements?.filter(
                (p) => p.placement !== placement
            ) || [];
        const newPlacements = enabled ? [...filtered, { placement }] : filtered;

        review = {
            ...review,
            User_Review_Placements: newPlacements
        };
    };
</script>

<blockquote class="flex flex-col gap-4 {className}">
    <Card
        p_lg
        {invert}
        class="rounded-bl {invert ? 'text-symbols-invert-default' : ''}"
    >
        <div class="flex flex-col gap-1" class:opacity-40={!review.approved}>
            {#if review.review_rating}
                <div class="text-2xl {invert ? 'text-yellow-500 dark:text-yellow-400' : 'text-yellow-400'}">
                    {#each Array(5) as _, i}
                        {i < review.review_rating ? '★' : '☆'}
                    {/each}
                </div>
            {/if}
            <Prose lg {invert}>
                {@html text}

                {#if collapse && collapsed}
                    <Button on:click={handle_expand} link nop>Read more</Button>
                {/if}
            </Prose>
        </div>
    </Card>
    <cite class="text-base px-1 md:px-6">
        &mdash; {review.username}
        {#if review.for_name && show_for}
            for <a href={review.for_path} class="link">{review.for_name}</a>
        {/if}
    </cite>
    {#await is_admin then is_admin}
        {#if is_admin}
            <div
                class="flex flex-col gap-3 justify-self-end border-t border-border-muted pt-4"
            >
                <div class="flex gap-2 text-sm text-gray-600">
                    <Button
                        xs
                        outline
                        on:click={toggle_approved}
                        disabled={toggling}
                    >
                        {review.approved ? 'Unapprove' : 'Approve'}
                    </Button>

                    <input
                        type="number"
                        class="!text-sm fit-content !w-16 !px-2 !py-1"
                        value={priority_value}
                        on:change={handle_priority_change}
                        disabled={updating_priority}
                    />
                </div>

                <div class="flex flex-col gap-2 text-sm text-gray-600">
                    <label class="flex items-center gap-2">
                        <input
                            type="checkbox"
                            class="bg-transparent"
                            checked={placement_home}
                            on:change={(e) =>
                                update_placement(
                                    'homepage',
                                    e.currentTarget.checked
                                )}
                            disabled={updating_placements}
                        />
                        <span>Show on home</span>
                    </label>
                    <label class="flex items-center gap-2">
                        <input
                            type="checkbox"
                            class="bg-transparent"
                            checked={placement_list}
                            on:change={(e) =>
                                update_placement(
                                    'product_list',
                                    e.currentTarget.checked
                                )}
                            disabled={updating_placements}
                        />
                        <span>Show on list</span>
                    </label>
                </div>
            </div>
        {/if}
    {/await}
</blockquote>

<style>
    blockquote :global(p) {
        margin-bottom: calc(var(--spacing) * 2);
    }
</style>
